import { Information } from '@carbon/icons-react';
import { Button, InlineNotification, NumberInput } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { formulaDetailsAtomFamily } from 'atoms/formula-details-atoms';
import { formatNumber } from 'utils/number';
import {
  acreageDraftStashAtom,
  describeSaveFailure,
  deviatesBeyondTolerance,
  DRIFT_PRECISION,
  OVERLAP_TOLERANCE,
  recalculationDeferred,
  recalculationFailed,
  saveAcreageBasisAtom,
  type SaveResponse,
} from '../tract-intersection-analysis/acreage-basis-atoms';
import styles from './uat-acreage-basis-panel.module.scss';

/**
 * Why the calculation could not apply legal acreage, keyed by the warning symbol
 * NriCalculation::AcreageBasisResolver emits. When one of these is present the
 * row is stored as legal but was computed on GIS, and without saying so the only
 * visible symptom is figures that never match the chosen basis.
 */
const BLOCKED_REASONS: Record<string, string> = {
  no_allocation_manager_acreage:
    'the Allocation Manager records no plat acreage for this tract',
  no_tract_gross_acres: 'the Asset has no gross acreage recorded',
  no_measured_overlap:
    'the shapes measure no overlap between the Asset and this tract',
};

/** The precision a division order is written in, and what the mock shows. */
const FACTOR_PRECISION = 4;
const ACRE_PRECISION = 2;

/**
 * The scaling factor as the field shows it.
 *
 * @param {number | null} value - The factor in effect
 * @return {number | ''} The rounded factor, or '' when there is none
 */
const displayFactor = (value: number | null): number | '' => {
  if (value === null) return '';

  return Number(value.toFixed(FACTOR_PRECISION));
};

/**
 * The same fact expressed as acres.
 *
 * @param {number | null} value - The factor in effect
 * @param {number | null} tractGrossAc - The document's gross acreage
 * @return {number | ''} The scaled acreage, or '' when either input is absent
 */
const displayAcres = (
  value: number | null,
  tractGrossAc: number | null
): number | '' => {
  if (value === null || tractGrossAc === null) return '';

  return Number((value * tractGrossAc).toFixed(ACRE_PRECISION));
};

interface UatAcreageBasisPanelProps {
  subtractId: string;
  uatGisUuid: string;
  /** Identifies the cached formula payload this panel is rendered from. */
  nriCalculationId: number;
  /** Closes the popup. Done saves and closes in one action. */
  onDone: () => void;
  /** Document gross acres — the legal-basis numerator before scaling. */
  tractGrossAc: number | null;
  /** The UAT's plat acreage, which the participation cannot exceed. */
  uatPlatAc: number | null;
  /** Scaling factor currently in effect, whether measured or overridden. */
  overlapFraction: number | null;
  /** What the shapes measure, shown as the baseline to compare against. */
  gisOverlapFraction: number | null;
  /**
   * The stamped calculation still describes the previous choice, because the
   * recalculation a save queues has not finished. The worked equation below
   * this editor is built from that stamp, so it would otherwise contradict the
   * fields above it.
   */
  recalculating?: boolean;
  /**
   * Warning symbols stamped on the calculation. A blocking one means legal basis
   * was requested but not applied, which is a permanent state rather than a
   * pending one — so it must not be reported as "recalculating".
   */
  warnings?: string[];
  /**
   * The deviation rule, served rather than mirrored — the backend enforces the
   * same threshold when it discards a drifted override. Optional so a payload
   * predating the served fields still renders.
   */
  overlapTolerance?: number;
  driftPrecision?: number;
}

/**
 * Lets the land team adjust how much of the tract counts as inside this UAT,
 * under legal acreage. Its purpose is rounding to the clean numbers a division
 * order carries — 0.5031 to 0.50 — not material restatement, which is why a
 * large deviation warns rather than blocks.
 *
 * The factor and the acres are two views of one fact and stay in sync. Only the
 * factor is persisted: it is a geometric assertion, while Tract Gross is a
 * document figure that can be corrected independently.
 *
 * @param {UatAcreageBasisPanelProps} props - The component props.
 * @return {JSX.Element} The scaling factor editor.
 */
export const UatAcreageBasisPanel = ({
  subtractId,
  uatGisUuid,
  nriCalculationId,
  onDone,
  tractGrossAc,
  uatPlatAc,
  overlapFraction,
  gisOverlapFraction,
  recalculating = false,
  warnings = [],
  overlapTolerance = OVERLAP_TOLERANCE,
  driftPrecision = DRIFT_PRECISION,
}: UatAcreageBasisPanelProps): JSX.Element => {
  const save = useSetAtom(saveAcreageBasisAtom);

  // The factor the calculation is using: the user's own where they have saved
  // one, otherwise what the shapes measure — which is the default legal basis
  // applies. `overlapFraction` comes from the stored parameter row, correct the
  // instant the save returns, so the drafts can simply follow it.
  //
  // Falling back matters on a UAT just switched to legal: it has no override
  // yet, and if the row has not been restamped the payload carries no overlap
  // either. Both fields then rendered empty, which Carbon shows as an invalid
  // number input — a red box where a default belongs.
  const effectiveFactor = overlapFraction ?? gisOverlapFraction;

  // Both drafts follow the *displayed* factor, not the stored one. Seeding the
  // acres from full precision put three different numbers for one quantity on
  // screen — the acres field said 79.89, the explanation sentence said 79.88
  // because it multiplies the rounded factor, and the server stored a third — and
  // the exceeds-plat check ran on the sentence's figure rather than the field's.
  const shownFactor = displayFactor(effectiveFactor);

  // BaseCard closes this popup on any outside click, unconditionally — the
  // panel cannot intercept that. What it can do is make the loss recoverable:
  // a dirty unmount stashes the draft (see the cleanup effect below) and the
  // next open re-seeds from it, dirty hint and all. Escape and a successful
  // save clear the stash — those are deliberate exits.
  const stashKey = `${subtractId}:${uatGisUuid}`;
  const stashedDraft = useAtomValue(acreageDraftStashAtom)[stashKey];
  const setDraftStash = useSetAtom(acreageDraftStashAtom);

  const seededFactor = stashedDraft ?? shownFactor;
  const [draftFactor, setDraftFactor] = useState<number | ''>(seededFactor);
  const [draftAcres, setDraftAcres] = useState<number | ''>(
    displayAcres(seededFactor === '' ? null : seededFactor, tractGrossAc)
  );

  // Skipped on mount: the deps effect would otherwise run once immediately and
  // reset the just-seeded stash back to the stored factor.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    const shown = displayFactor(effectiveFactor);
    setDraftFactor(shown);
    setDraftAcres(displayAcres(shown === '' ? null : shown, tractGrossAc));
  }, [effectiveFactor, tractGrossAc]);

  // The resolver emits at most one blocker, but it shares the array with the
  // advisory warnings, so pick the first that names a blocking cause.
  const blockedReason =
    warnings.map((code) => BLOCKED_REASONS[code]).find(Boolean) ?? null;

  // Refs, because focus is the one thing React state cannot express. BaseCard
  // owns dismissal and is shared by five other surfaces, so this is kept local
  // to the panel rather than changing that behaviour for all of them.
  const containerRef = useRef<HTMLDivElement>(null);

  // The popup opens with focus left wherever it was, so a keyboard user has no
  // way in and nothing to return to on the way out.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    containerRef.current?.querySelector('input')?.focus();

    return () => previouslyFocused?.focus?.();
  }, []);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Kept apart from saveError. Both of these follow a save that *succeeded* —
  // reporting them as "Not saved" contradicts the message they carry.
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const factor = draftFactor === '' ? null : draftFactor;
  const acres =
    factor !== null && tractGrossAc !== null ? tractGrossAc * factor : null;

  const exceedsUat = acres !== null && uatPlatAc !== null && acres > uatPlatAc;
  const deviates = deviatesBeyondTolerance(
    factor,
    gisOverlapFraction,
    overlapTolerance,
    driftPrecision
  );
  // Compared at the precision the field displays, or a factor that does not
  // round cleanly would read as edited the moment the popup opened, and Done
  // would pin it as an override.
  const dirty = draftFactor !== displayFactor(effectiveFactor);

  // An UNANNOUNCED unmount (BaseCard's outside-click close) stashes the draft
  // while dirty. The deliberate exits — Escape (discard) and a successful Done
  // (stored) — set the suppress flag instead: a flag rather than a per-render
  // value, because React may re-render between the handler and the unmount
  // (setSaving(false) does exactly that) and a per-render assignment would
  // re-arm the stash from the not-yet-refreshed props.
  const dirtyRef = useRef(dirty);
  const draftRef = useRef(draftFactor);
  dirtyRef.current = dirty;
  draftRef.current = draftFactor;
  const suppressStashRef = useRef(false);

  useEffect(
    () => () => {
      const stash =
        !suppressStashRef.current && dirtyRef.current ? draftRef.current : null;
      setDraftStash((prev) => {
        if (stash === null) {
          if (!(stashKey in prev)) return prev;
          const rest = { ...prev };
          delete rest[stashKey];
          return rest;
        }
        return { ...prev, [stashKey]: stash };
      });
    },
    [setDraftStash, stashKey]
  );

  /**
   * Escape discards and closes, which is what a click outside already does —
   * this only makes it reachable from the keyboard. Stopped here so it does not
   * also close whatever the popup is rendered inside.
   *
   * @param {React.KeyboardEvent} event - The keydown event
   * @return {void}
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') return;

    event.stopPropagation();
    // Escape is an EXPLICIT discard — unlike an outside click, it must not
    // stash the draft for the next open.
    suppressStashRef.current = true;
    onDone();
  };

  /**
   * Editing either field is the same assertion expressed the other way round,
   * so each keeps the other in step without saving anything.
   *
   * @param {number | ''} next - The scaling factor the user typed
   * @return {void}
   */
  const changeFactor = (next: number | '') => {
    setDraftFactor(next);
    setDraftAcres(displayAcres(next === '' ? null : next, tractGrossAc));
  };

  /**
   * @param {number | ''} nextAcres - Participation acres the user typed
   * @return {void}
   */
  const changeAcres = (nextAcres: number | '') => {
    if (nextAcres === '' || tractGrossAc === null || tractGrossAc === 0) {
      setDraftAcres(nextAcres);
      setDraftFactor('');
      return;
    }

    // The 4dp factor is the unit of persistence, so the acres field is snapped
    // to whatever that factor means rather than keeping the typed number. Left
    // alone the two drift apart — 79.89 typed implies a factor that means 79.90
    // — and an acres edit that rounds back to the stored factor would close the
    // popup having silently saved nothing.
    const nextFactor = Number(
      (nextAcres / tractGrossAc).toFixed(FACTOR_PRECISION)
    );
    setDraftFactor(nextFactor);
    setDraftAcres(displayAcres(nextFactor, tractGrossAc));
  };

  /**
   * The one commit point. Saving is consequential — it queues a recalculation
   * and moves NRI — so it happens on an explicit action rather than on blur,
   * Enter, a stepper click or an unmount, all of which this replaces.
   *
   * Awaited rather than fired and forgotten. It is a single PUT that returns in
   * well under a second, so waiting costs the user nothing, and it buys two
   * things: the row is committed and the payload refresh is already under way
   * before the popup closes, and a rejected save keeps the popup open with the
   * reason instead of closing as though it had worked — which is
   * indistinguishable, from the outside, from the value silently reverting.
   *
   * @return {Promise<void>} resolves once the save has been attempted
   */
  const handleDone = async () => {
    if (!dirty) {
      onDone();
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveNotice(null);

    const response: SaveResponse | undefined = await save({
      subtractId,
      uatGisUuid,
      acreageBasis: 'legal',
      overlapFraction: factor,
    });

    setSaving(false);

    if (response?.type !== 'success') {
      setSaveError(
        describeSaveFailure(response, 'The scaling factor could not be saved.')
      );
      return;
    }

    // The factor is stored, but the figures it drives are not. Holding the popup
    // open is the point: closing it would leave the card showing the old NRI
    // with no indication the two no longer correspond.
    // Both leave the popup open: the value is stored, but the figures below it
    // are not the ones it produces, and closing would hide that.
    if (recalculationFailed(response)) {
      setSaveNotice(
        'The interest figures could not be recalculated, so they still show the previous value.'
      );
      return;
    }

    if (recalculationDeferred(response)) {
      setSaveNotice(
        'This Asset has too many calculations to update in one request, so the interest figures are being recalculated in the background — reload in a moment to see them.'
      );
      return;
    }

    // The formula line is built from the recalculated metadata, so the cached
    // payload is dropped to pick it up on the next open.
    formulaDetailsAtomFamily.remove(nriCalculationId);
    // Stored now — a stale stash would resurrect the pre-save draft.
    suppressStashRef.current = true;
    onDone();
  };

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onKeyDown={handleKeyDown}
      role="group"
      aria-label="Legal acreage scaling"
    >
      {/*
        Only when the scaling actually removes something. At a factor of 1 the
        whole Asset sits inside the UAT and the note claimed the opposite.
      */}
      {tractGrossAc !== null && factor !== null && factor < 1 && (
        <div className={styles.explanation}>
          <Information size={16} className={styles.explanationIcon} />
          <span>
            Only part of the Asset falls within the UAT — the Legal acreage is
            scaled to the correct value (scaling factor{' '}
            {formatNumber(factor, FACTOR_PRECISION, FACTOR_PRECISION)}:{' '}
            {formatNumber(tractGrossAc, ACRE_PRECISION, ACRE_PRECISION)} ac
            &times; {formatNumber(factor, FACTOR_PRECISION, FACTOR_PRECISION)} ={' '}
            {acres === null
              ? '--'
              : formatNumber(acres, ACRE_PRECISION, ACRE_PRECISION)}{' '}
            ac).
          </span>
        </div>
      )}

      <div className={styles.inputs}>
        <div className={styles.field}>
          <NumberInput
            id={`scaling-factor-${uatGisUuid}`}
            size="sm"
            label="Scaling factor"
            // Carbon defaults allowEmpty to false, which turns a cleared field
            // into Number('') === 0. That PUTs overlap_fraction: 0, which the
            // model rejects as "must be greater than 0" — so clearing an
            // override, the one way back to following the shapes, reported a
            // validation error instead.
            allowEmpty
            step={0.0001}
            min={0}
            max={1}
            value={draftFactor}
            onChange={(_e: unknown, { value }: { value: number | string }) =>
              changeFactor(value === '' ? '' : Number(value))
            }
          />
        </div>
        <div className={styles.field}>
          <NumberInput
            id={`participation-acres-${uatGisUuid}`}
            size="sm"
            label="Participation acres"
            allowEmpty
            step={0.01}
            min={0}
            value={draftAcres}
            onChange={(_e: unknown, { value }: { value: number | string }) =>
              changeAcres(value === '' ? '' : Number(value))
            }
          />
        </div>
      </div>

      {(deviates || exceedsUat) && (
        <div className={styles.warnings}>
          {deviates && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Differs from the mapped shapes"
              subtitle={`This is more than ${
                overlapTolerance * 100
              }% away from the measured ${
                gisOverlapFraction === null
                  ? 'overlap'
                  : formatNumber(
                      gisOverlapFraction,
                      FACTOR_PRECISION,
                      FACTOR_PRECISION
                    )
              }. It will be saved, but check it is the right tool for the correction you are making.`}
            />
          )}

          {exceedsUat && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Exceeds the UAT acreage"
              subtitle="Participation cannot be larger than the UAT, so the calculation caps it at the UAT's plat acreage."
            />
          )}
        </div>
      )}

      {blockedReason && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Legal acreage was not applied"
          subtitle={`This was calculated on GIS acreage because ${blockedReason}. The figures below are GIS-based.`}
        />
      )}

      {/*
        Only meaningful when nothing is blocking. A blocked row disagrees with
        its stamp permanently, so reporting it as pending would promise an update
        that never arrives.
      */}
      {recalculating && !blockedReason && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Figures are behind the current setting"
          subtitle="The equation below still describes the previous setting. Reopen this popup to pick up the recalculated figures."
        />
      )}

      {saveError && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Not saved"
          subtitle={saveError}
        />
      )}

      {saveNotice && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Saved, but the figures are not current"
          subtitle={saveNotice}
        />
      )}

      <div className={styles.actions}>
        {/*
          Dismissal does not save, and clicking away is easy to do by accident.
          Saying so is the smallest honest fix: blocking the dismissal instead
          would trap the user in a popup they cannot leave.
        */}
        {dirty && (
          <span className={styles.unsaved}>
            Unsaved — Done to save, Escape to discard
          </span>
        )}
        <Button size="sm" kind="primary" onClick={handleDone} disabled={saving}>
          {saving ? 'Saving…' : 'Done'}
        </Button>
      </div>
    </div>
  );
};
